import type { ShareCardPattern } from '@tabbio/types';

/**
 * Vector art for the CV share card, shared by the native card
 * (react-native-svg), the web card (inline <svg>) and the OG image (satori).
 * Every drawing is ONE fill-only path with explicit winding (outer contours
 * clockwise, holes counter-clockwise, non-zero rule) so each renderer draws
 * it the same way without strokes, masks or nested elements. The strokes of
 * the design repo's SVGs (assets/share-form-briefcase.svg,
 * share-cv-background.svg) were converted to outlines by hand.
 */

export interface ShareCardSvgArt {
  viewBox: string;
  width: number;
  height: number;
  path: string;
}

/**
 * The Tabbio "t" monogram. Same path as apps/mobile TabbioMark.tsx and
 * packages/public-renderers cv-contact-icon.tsx: keep the three in sync.
 */
export const SHARE_CARD_MARK_ART: ShareCardSvgArt = {
  viewBox: '0 0 24.0361 27.9998',
  width: 24.0361,
  height: 27.9998,
  path:
    'M5.05762 0.0242382C8.24806 -0.0283878 11.5976 0.0210352 14.8027 0.0213085C14.7806 1.07161 14.8009 2.19575 14.8008 3.2508L14.7998 9.52326L21.5635 9.52619L21.5596 14.9774L14.8018 14.9754L14.7998 22.5067L24.0361 22.5096L24.0342 26.2781L24.0322 27.9979L17.8818 27.9998C15.2273 27.9971 13.0641 27.6624 11.0742 25.6922C9.10929 23.7465 9.30806 21.8741 9.30957 19.3055L9.31348 14.9744L0.000976562 14.9754L0 5.05842C1.68873 3.43561 3.35518 1.61203 5.05762 0.0242382ZM5.71582 5.82111L2.00977 5.81818V9.52424L5.62207 9.52521L9.31055 9.52717L9.31348 1.95197L5.71973 1.951L5.71582 5.82111Z',
};

/** A document with a folded corner, a portrait and text lines (design: share-cv-background.svg). */
export const SHARE_CARD_CV_ART: ShareCardSvgArt = {
  viewBox: '0 0 160 200',
  width: 160,
  height: 200,
  path: [
    // Sheet outline, 5 wide: outer contour clockwise, inner counter-clockwise.
    'M22 4.5H106L140.5 39V183A12.5 12.5 0 0 1 128 195.5H22A12.5 12.5 0 0 1 9.5 183V17A12.5 12.5 0 0 1 22 4.5Z',
    'M22 9.5A7.5 7.5 0 0 0 14.5 17V183A7.5 7.5 0 0 0 22 190.5H128A7.5 7.5 0 0 0 135.5 183V41L104 9.5Z',
    // Folded corner, 5 wide.
    'M107.5 7V32A5.5 5.5 0 0 0 113 37.5H138V42.5H113A10.5 10.5 0 0 1 102.5 32V7Z',
    // Portrait.
    'M40 45A12 12 0 1 1 40 69A12 12 0 1 1 40 45Z',
    // Text lines, 6 wide with round ends.
    'M65 49H112A3 3 0 0 1 112 55H65A3 3 0 0 1 65 49Z',
    'M65 61H95A3 3 0 0 1 95 67H65A3 3 0 0 1 65 61Z',
    'M29 90H111A3 3 0 0 1 111 96H29A3 3 0 0 1 29 90Z',
    'M29 103H94A3 3 0 0 1 94 109H29A3 3 0 0 1 29 103Z',
    'M29 130H111A3 3 0 0 1 111 136H29A3 3 0 0 1 29 130Z',
    'M29 143H101A3 3 0 0 1 101 149H29A3 3 0 0 1 29 143Z',
    'M29 156H85A3 3 0 0 1 85 162H29A3 3 0 0 1 29 156Z',
  ].join(''),
};

/** An outlined briefcase (design: share-form-briefcase.svg; storage key `arch`). */
export const SHARE_CARD_BRIEFCASE_ART: ShareCardSvgArt = {
  viewBox: '0 0 240 200',
  width: 240,
  height: 200,
  path: [
    // Body, 9 wide: outer contour clockwise, inner counter-clockwise.
    'M44 41.5H196A26.5 26.5 0 0 1 222.5 68V155.5A26.5 26.5 0 0 1 196 182.5H44A26.5 26.5 0 0 1 17.5 155.5V68A26.5 26.5 0 0 1 44 41.5Z',
    'M44 50.5A17.5 17.5 0 0 0 26.5 68V156A17.5 17.5 0 0 0 44 173.5H196A17.5 17.5 0 0 0 213.5 156V68A17.5 17.5 0 0 0 196 50.5Z',
    // Handle, 9 wide, flush with the body.
    'M82.5 41.5V31A17.5 17.5 0 0 1 100 13.5H140A17.5 17.5 0 0 1 157.5 31V41.5H148.5V31A8.5 8.5 0 0 0 140 22.5H100A8.5 8.5 0 0 0 91.5 31V41.5Z',
    // Belt, 9 wide, spanning the inner width.
    'M26.5 91C80 117.5 160 117.5 213.5 91V100C160 126.5 80 126.5 26.5 100Z',
    // Clasp (its white fill reads as solid in the design's alpha mask).
    'M111 98.5H129A10.5 10.5 0 0 1 139.5 109V122A10.5 10.5 0 0 1 129 132.5H111A10.5 10.5 0 0 1 100.5 122V109A10.5 10.5 0 0 1 111 98.5Z',
  ].join(''),
};

/** Three wave bands (design: share-form-wave.svg). */
export const SHARE_CARD_WAVE_ART: ShareCardSvgArt = {
  viewBox: '0 0 240 240',
  width: 240,
  height: 240,
  path:
    'M0 40C60-10 180 90 240 40V90C180 140 60 40 0 90ZM0 120C60 70 180 170 240 120V170C180 220 60 120 0 170ZM0 200C60 150 180 250 240 200V240H0Z',
};

/** The placeholder avatar: a filled head and shoulders (design: og-avatar-placeholder). */
export const SHARE_CARD_AVATAR_PLACEHOLDER_ART: ShareCardSvgArt = {
  viewBox: '0 0 24 24',
  width: 24,
  height: 24,
  path: 'M12 3A4 4 0 1 1 12 11A4 4 0 1 1 12 3ZM4 21A8 8 0 0 1 20 21H4Z',
};

/** Card box used by the full-bleed orbit art: 1.91:1. */
export const SHARE_CARD_BOX = { width: 1910, height: 1000 } as const;

function ellipse(cx: number, cy: number, rx: number, ry: number, clockwise: boolean): string {
  const sweep = clockwise ? 1 : 0;
  const r = (n: number) => n.toFixed(1);
  return `M${r(cx - rx)} ${r(cy)}A${r(rx)} ${r(ry)} 0 1 ${sweep} ${r(cx + rx)} ${r(cy)}A${r(rx)} ${r(ry)} 0 1 ${sweep} ${r(cx - rx)} ${r(cy)}Z`;
}

/**
 * Three thin rings centred at 110%/110% of the card, the fill-only twin of
 * the design's `radial-gradient(ellipse at 110% 110%, ...)` with stops at
 * 35/44/53% of the farthest-corner radius and a 0.4% ring width.
 */
function buildOrbitPath(): string {
  const { width, height } = SHARE_CARD_BOX;
  const cx = width * 1.1;
  const cy = height * 1.1;
  // farthest-corner ellipse, aspect = card aspect: passes through (0,0).
  const k = 1.1 * Math.SQRT2;
  const parts: string[] = [];
  for (const stop of [0.35, 0.44, 0.53]) {
    const outer = stop + 0.004;
    parts.push(ellipse(cx, cy, k * width * outer, k * height * outer, true));
    parts.push(ellipse(cx, cy, k * width * stop, k * height * stop, false));
  }
  return parts.join('');
}

/** Full-bleed orbit rings, drawn in the SHARE_CARD_BOX space. */
export const SHARE_CARD_ORBIT_ART: ShareCardSvgArt = {
  viewBox: `0 0 ${SHARE_CARD_BOX.width} ${SHARE_CARD_BOX.height}`,
  width: SHARE_CARD_BOX.width,
  height: SHARE_CARD_BOX.height,
  path: buildOrbitPath(),
};

/**
 * The edit control's brush (design: share-edit-brush.svg without its white
 * disc), two stroked paths. Same glyph as apps/mobile BrushIcon.tsx in a
 * 32-unit box so the 27px rendering keeps the design's 1.5 stroke.
 */
export const SHARE_CARD_BRUSH_ICON = {
  viewBox: '0 0 32 32',
  strokeWidth: 1.5,
  paths: [
    'M13.9551 16.0437L20.5331 9.46575C21.1541 8.84475 22.1621 8.84475 22.7831 9.46575C23.4041 10.0867 23.4041 11.0947 22.7831 11.7157L16.2051 18.2937',
    'M8.75 21.7065C11.453 22.5185 13.646 22.5865 15.439 20.7515C16.52 19.6665 16.52 17.9065 15.439 16.8205C14.358 15.7345 12.613 15.7185 11.523 16.8205C9.75 18.6125 11.298 20.3145 8.75 21.7065Z',
  ],
} as const;

/** lucide arrow-up-right and download, each merged into one path (stroke 2). */
export const SHARE_CARD_CTA_ICON_PATHS = {
  view: 'M7 7h10v10M7 17 17 7',
  download: 'M12 15V3M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5',
} as const;

/**
 * Where a silhouette sits, as fractions of the card box (the design's CSS
 * `inset: auto <right> <bottom> auto; width; height` with the art contained
 * and centred in that box, then rotated about the box centre).
 */
export interface ShareCardSilhouetteSpec {
  art: ShareCardSvgArt;
  /** Offset of the box's end edge past the card's end edge (negative = overflow). */
  right: number;
  bottom: number;
  width: number;
  height: number;
  rotate: number;
}

export type ShareCardSilhouettePattern = Exclude<ShareCardPattern, 'none' | 'orbit'>;

export const SHARE_CARD_SILHOUETTES: Record<ShareCardSilhouettePattern, ShareCardSilhouetteSpec> = {
  signature: { art: SHARE_CARD_MARK_ART, right: -0.05, bottom: -0.28, width: 0.58, height: 1.4, rotate: -12 },
  cv: { art: SHARE_CARD_CV_ART, right: 0.04, bottom: -0.2, width: 0.44, height: 1.12, rotate: -12 },
  arch: { art: SHARE_CARD_BRIEFCASE_ART, right: -0.06, bottom: -0.16, width: 0.58, height: 1, rotate: -12 },
  wave: { art: SHARE_CARD_WAVE_ART, right: -0.04, bottom: -0.28, width: 0.52, height: 1.35, rotate: -12 },
};

export interface ShareCardSilhouetteLayout {
  art: ShareCardSvgArt;
  /** The rotated box, in card pixels from the card's top/left. */
  box: { left: number; top: number; width: number; height: number };
  /** The art, aspect-fitted and centred inside the box (box-relative pixels). */
  inner: { x: number; y: number; width: number; height: number };
  rotate: number;
}

/** Fit `art` into a box (CSS `contain`), centred. */
export function containShareCardArt(
  art: ShareCardSvgArt,
  boxWidth: number,
  boxHeight: number,
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(boxWidth / art.width, boxHeight / art.height);
  const width = art.width * scale;
  const height = art.height * scale;
  return { x: (boxWidth - width) / 2, y: (boxHeight - height) / 2, width, height };
}

/** A hex ink with an alpha, as `rgba(...)` (every renderer understands it). */
export function shareCardInkAlpha(hex: string, alpha: number): string {
  let c = hex.replace('#', '').trim();
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  if (!/^[0-9a-fA-F]{6}$/.test(c)) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Layout metrics ──────────────────────────────────────────

/** The design's reference card width; fixed sizes below are in its pixels. */
export const SHARE_CARD_DESIGN_WIDTH = 362;
export const SHARE_CARD_ASPECT = 1.91;
export const SHARE_CARD_RADIUS = 18;

export interface ShareCardFluidSpec {
  min: number;
  /** Percent of the card width (CSS `cqi`). */
  cqi: number;
  max: number;
}

/** Sizes that scale with the card width (the design's `clamp(min, Ncqi, max)`). */
export const SHARE_CARD_FLUID = {
  padding: { min: 12, cqi: 4, max: 18 },
  linkFont: { min: 10, cqi: 3, max: 12 },
  avatar: { min: 46, cqi: 17, max: 70 },
  identityGap: { min: 10, cqi: 3.5, max: 16 },
  nameFont: { min: 16, cqi: 5.5, max: 23 },
  roleFont: { min: 10, cqi: 3.1, max: 13 },
  ctaFont: { min: 9, cqi: 3, max: 12 },
} as const satisfies Record<string, ShareCardFluidSpec>;

/** Sizes fixed at the design width (scaled only for the OG image). */
export const SHARE_CARD_FIXED = {
  linkRow: 22,
  favicon: 12,
  rowGap: 8,
  ctaMinHeight: 30,
  ctaPaddingX: 14,
  ctaGap: 4,
  ctaIcon: 14,
  hairline: 1,
  editHit: 44,
  editInset: 7,
  editIcon: 27,
  editOffset: 10,
  nameLetterSpacing: -0.5,
} as const;

export type ShareCardMetrics = Record<keyof typeof SHARE_CARD_FLUID | keyof typeof SHARE_CARD_FIXED, number>;

/** CSS for a fluid size, for renderers with container-query units. */
export function shareCardFluidCss(spec: ShareCardFluidSpec): string {
  return `clamp(${spec.min}px, ${spec.cqi}cqi, ${spec.max}px)`;
}

/**
 * Every size for a card of `width` pixels. The app clamps like the design;
 * the OG image passes `clamp: false` and `fixedScale: 1200 / 362` so the
 * 1200px picture is the design card scaled up.
 */
export function shareCardMetrics(
  width: number,
  options: { clamp?: boolean; fixedScale?: number } = {},
): ShareCardMetrics {
  const clamp = options.clamp ?? true;
  const fixedScale = options.fixedScale ?? 1;
  const out: Record<string, number> = {};
  for (const [key, spec] of Object.entries(SHARE_CARD_FLUID) as [string, ShareCardFluidSpec][]) {
    const fluid = (width * spec.cqi) / 100;
    out[key] = clamp ? Math.min(spec.max, Math.max(spec.min, fluid)) : fluid;
  }
  for (const [key, value] of Object.entries(SHARE_CARD_FIXED)) {
    out[key] = value * fixedScale;
  }
  return out as ShareCardMetrics;
}

/**
 * Pixel layout of a silhouette for a card of the given size. Renderers that
 * cannot use percentage insets (satori, native) draw from this.
 */
export function layoutShareCardSilhouette(
  pattern: ShareCardPattern,
  cardWidth: number,
  cardHeight: number,
): ShareCardSilhouetteLayout | null {
  if (pattern === 'none' || pattern === 'orbit') return null;
  const spec = SHARE_CARD_SILHOUETTES[pattern];
  const width = cardWidth * spec.width;
  const height = cardHeight * spec.height;
  const left = cardWidth - width - cardWidth * spec.right;
  const top = cardHeight - height - cardHeight * spec.bottom;
  return {
    art: spec.art,
    box: { left, top, width, height },
    inner: containShareCardArt(spec.art, width, height),
    rotate: spec.rotate,
  };
}
